/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { Button, Text } from '@/shared/ui';
import { channelDotClass, type ChannelSummary } from './channel-groups';
import type { TranslateFn } from './publication-row-parts';

interface TileProps {
  label: string;
  value: number;
  suffix?: string;
  tone?: 'default' | 'attention';
  testId: string;
}

/** One summary figure tile; an attention tile turns red when its value is above zero. */
function SummaryTile({ label, value, suffix, tone = 'default', testId }: TileProps) {
  const valueColor = tone === 'attention' && value > 0 ? 'text-(--status-error-text)' : 'text-(--seed-base)';
  return (
    <div className="flex flex-col gap-(--space-0-5) rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-primary) p-(--space-3)" data-testid={testId}>
      <Text as="span" className="text-xs text-(--text-tertiary)">{label}</Text>
      <Text as="span" className={`text-lg font-bold ${valueColor}`}>
        {value}
        {suffix && <Text as="span" className="ml-(--space-1) text-xs font-normal text-(--text-tertiary)">{suffix}</Text>}
      </Text>
    </div>
  );
}

/**
 * The strip above the channel groups: how many channels are live, how many
 * publications there are, how many run a paid ad and how many need attention.
 * Everything here is computed from the publication rows themselves.
 */
export function ChannelSummaryStrip({ summary, t }: { summary: ChannelSummary; t: TranslateFn }) {
  return (
    <div className="grid grid-cols-2 gap-(--space-3) sm:grid-cols-4" data-testid="channel-summary">
      <SummaryTile
        testId="summary-live-channels"
        label={t('publications.summary.liveChannels')}
        value={summary.liveChannels}
        suffix={t('publications.summary.ofTotal', { total: summary.totalChannels })}
      />
      <SummaryTile testId="summary-publications" label={t('publications.summary.publications')} value={summary.publications} />
      <SummaryTile testId="summary-active-ads" label={t('publications.summary.activeAds')} value={summary.withActiveAds} />
      <SummaryTile testId="summary-attention" label={t('publications.summary.needingAttention')} value={summary.needingAttention} tone="attention" />
    </div>
  );
}

/** Heading of one channel family ("Redes sociais", "Portais"). */
export function ChannelGroupTitle({ title, testId }: { title: string; testId: string }) {
  return (
    <Text as="p" className="m-0 text-xs font-bold text-(--text-secondary)" data-testid={testId}>
      {title}
    </Text>
  );
}

interface NotPublishedProps {
  portals: string[];
  getPortalLabel: (portal: string) => string;
  onPublish: () => void;
  t: TranslateFn;
}

/**
 * Channels the agency has connected where this property is not published yet,
 * each with a shortcut to publish there.
 */
export function NotPublishedSection({ portals, getPortalLabel, onPublish, t }: NotPublishedProps) {
  if (portals.length === 0) return null;
  return (
    <div className="flex flex-col gap-(--space-2)" data-testid="not-published-section">
      <ChannelGroupTitle title={t('publications.notPublished.title')} testId="not-published-title" />
      {portals.map((portal) => (
        <div
          key={portal}
          className="flex items-center justify-between gap-(--space-2) rounded-(--radius-lg) border border-dashed border-(--border-default) p-(--space-3)"
          data-testid={`not-published-${portal}`}
        >
          <div className="flex min-w-0 flex-wrap items-center gap-(--space-2)">
            <Text as="span" className={`h-2 w-2 shrink-0 rounded-(--radius-sm) ${channelDotClass(portal)}`} aria-hidden="true" />
            <Text as="span" className="text-xs font-semibold text-(--text-primary)">{getPortalLabel(portal)}</Text>
            <Text as="span" className="text-xs text-(--text-tertiary)">{t('publications.notPublished.hint')}</Text>
          </div>
          <Button
            type="button"
            variant="none"
            disableSize
            onClick={onPublish}
            className="shrink-0 cursor-pointer rounded-(--radius-md) border border-(--seed-base) bg-transparent px-(--space-2) py-(--space-1) text-xs font-semibold text-(--seed-base) hover:opacity-80"
            data-testid={`not-published-publish-${portal}`}
          >
            {t('publications.notPublished.publish')}
          </Button>
        </div>
      ))}
    </div>
  );
}
